import { Location } from "history";
import isNumber from "lodash-es/isNumber";
import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import {
    blogsFeedFetchTrigger,
    blogsFeedScrollToTrigger,
} from "@/mk2/apps/blogs/containers/BlogsFeed/BlogsFeed.actions";
import {
    getBlogsFeedPageState,
    getBlogsFeedSearchState,
    getBlogsFeedState,
    getBlogsFeedVirtualPageState,
} from "@/mk2/apps/blogs/containers/BlogsFeed/BlogsFeed.selectors";
import {
    blogsDeletePostTrigger,
    blogsIgnoreUserTrigger,
    blogsSetDisplayingAtFeedTrigger,
    blogsSetPostVisibilityTrigger,
} from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.actions";
import blogsSharedReducer from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.reducers";
import blogsSharedSaga from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.sagas";
import commentsSaga from "@/mk2/apps/blogs/containers/Comments/Comments.sagas";
import { BLOG_POST_MENU_MORE, BLOG_POST_VISIBILITY_MENU_MORE } from "@/mk2/apps/blogs/helpers";
import { blogFeedHeaderTabs } from "@/mk2/apps/blogs/utils";
import { PageRef } from "@/mk2/apps/forum/schemas";
import { parsePageNumber } from "@/mk2/apps/forum/utils";
import { emptyComplaintsCounts } from "@/mk2/apps/groups/containers/GroupReportedFeed/GroupReportedFeed.constants";
import {
    getCommentComplaintsCounts,
    getPostsCommentsCount,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.selectors";
import { Count } from "@/mk2/components/NotificationsBadge";
import { AppName } from "@/mk2/containers/Application/Application";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import {
    findComplaintsNonDeleted,
    getComplaintsNonDeleted,
    getComplaintsShownForBlogPosts,
} from "@/mk2/containers/Complaints/Complaints.selectors";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { FeedItem, FeedItemOrGroup } from "@/mk2/containers/PageHeader/PageHeader.reducers";
import { cacheLast, memoizeOne } from "@/mk2/helpers/cache";
import { UserAgentParsed } from "@/mk2/helpers/detects";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getPageHeaderState,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    getRequestUserAgent,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import {
    BlogPostEntity,
    BlogPostSchema,
    BlogPostType,
    ComplaintEntity,
    ComplaintTargetType,
    ComplaintWithoutTargetSchema,
    HeaderMenuItem,
    MenuSelection,
    PostVisibility,
    UserEntity,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { Store } from "@/mk2/store/configureStore";
import { BLOGS_WALL } from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import BlogsFeed, { getCategoryTitle } from "./BlogsFeed";
import reducer, {
    BlogsFeedPageState,
    BlogsFeedState,
    BlogsFeedVirtualPageState,
    initialBlogsFeedPageState,
} from "./BlogsFeed.reducers";
import saga from "./BlogsFeed.sagas";

export enum ScrollToPosition {
    FirstPost = "first",
    LastPost = "last",
}

export enum BlogsFeedCategory {
    ALL = "all",
    FLAGGED = "flagged",
    FLAGGED_GROUPS = "flagged-groups",
    MY_ACTIVITY = "my-activity",
    PR_ARTICLES = "pr-articles",
}

interface OwnProps {}

interface StateProps {
    brandDaysPostId: number;
    category: BlogsFeedCategory;
    commentsCounts: { [postId: number]: number };
    complaintsCounts: { [postId: number]: number };
    complaintsOpened: boolean[];
    postsSubtreeComplaintList: { [postId: number]: number[] };
    errorMessage: string;
    feedsTabsLastVisitCounts: {
        [tabName: string]: Count;
    };
    hasComplaints: boolean;
    hashtags: BoxedHashtags;
    isIOS: boolean;
    loadedAt: number;
    loadingState: LoadingState;
    isMobile: boolean;
    pageRef: PageRef;
    requestUserPermissions: string[];
    anyPageLoaded: boolean;
    posts: BlogPostEntity[];
    postType: BlogPostType;
    query: string;
    requestUser: UserEntity;
    userAgent: UserAgentParsed;
}

interface DispatchProps {
    onLoad(
        pageRef: PageRef,
        category: BlogsFeedCategory,
        postType: BlogPostType,
        query: string,
        currentUrl: Location,
        numPostsBeforeLoad: number,
    );
    onIgnoreUser(username: string);
    onPostToggleMenuMore(postId: number);
    onPostToggleVisibilityMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number, location: HistoryLocationWithState);
    onDelete(postId: number);
    onScrollTo(scrollto: ScrollToPosition);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onSetVisibility(postId: number, visibility: PostVisibility);
}

type RouteProps = PageRouteProps<{
    category?: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    postType: BlogPostType;
    query: string;
}

const emptyDict = {};

export default class BlogsFeedPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    private getPostsWithComplaintsCache = cacheLast(true);
    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = parse(qs) || {};
        let postType; // keep undefined if not in url, set null if incorrect
        if (qp["post-type"] !== undefined || qp.post_type !== undefined) {
            const postTypeStr = (qp["post-type"] as string) || (qp.post_type as string);
            postType =
                {
                    [BlogPostType.ALBUM.toString()]: BlogPostType.ALBUM,
                    [BlogPostType.SHORT_MESSAGE.toString()]: BlogPostType.SHORT_MESSAGE,
                    [BlogPostType.ARTICLE.toString()]: BlogPostType.ARTICLE,
                    [BlogPostType.AUTOMATIC_MESSAGE.toString()]: BlogPostType.AUTOMATIC_MESSAGE,
                    [BlogPostType.SHARE.toString()]: BlogPostType.SHARE,
                }[postTypeStr] || null;
        }
        return {
            postType,
            query:
                qp.query !== undefined
                    ? (qp.query as string)
                    : qp.h !== undefined
                      ? `#${qp.h as string}`
                      : null,
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const query = parsedQuery.query;
            const postType = parsedQuery.postType;
            const category =
                (ownProps.match.params.category as BlogsFeedCategory) || BlogsFeedCategory.ALL;
            const pageRef = parsePageNumber(ownProps.location);

            const pageHeaderState = getPageHeaderState(state);
            const feedsFeedItem: FeedItemOrGroup[] = pageHeaderState.feedMenu.filter(
                (fi) => fi.name === "feeds",
            );
            const feedsTabsLastVisitCounts =
                feedsFeedItem.length > 0
                    ? (feedsFeedItem[0] as FeedItem).tabsLastVisitCounts
                    : emptyDict;

            const blogsFeedState: BlogsFeedState = getBlogsFeedState(state);
            const blogsPostsState = getBlogsFeedSearchState(state, category, postType, query);

            // When navigating from HP with ?hp=<postId>, treat cached data as stale
            // if the first post doesn't match, so LoadingSwitch shows spinner instead of old data
            const hpParam = parse(ownProps.location.search).hp;
            const hpPostId = hpParam ? parseInt(hpParam as string, 10) : null;
            const page1State = blogsPostsState.pages[1];
            const hpMatchesCachedData =
                !hpPostId || (page1State && page1State.postIds[0] === hpPostId);
            const anyPageLoaded =
                hpMatchesCachedData && Object.keys(blogsPostsState.pages).length > 0;

            const requestUserPermissions = getRequestPermissions(state);
            const requestUser = getRequestUser(state);
            const userAgent = getRequestUserAgent(state);

            const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;

            const blogsFeedPageState: BlogsFeedPageState | BlogsFeedVirtualPageState = isNumber(
                pageRef,
            )
                ? getBlogsFeedPageState(state, category, postType, query, pageRef)
                : getBlogsFeedVirtualPageState(state, category, postType, query, pageRef);

            const postIds = isNumber(pageRef)
                ? (blogsFeedPageState as BlogsFeedPageState).postIds
                : initialBlogsFeedPageState.postIds;

            const loadedAt = (blogsFeedPageState as BlogsFeedPageState).loadedAt;
            const brandDaysPostId = (blogsFeedPageState as BlogsFeedPageState).brandDaysPostId;

            let posts: BlogPostEntity[] = getDenormalizedEntities<BlogPostEntity>(
                state,
                BlogPostSchema,
                postIds,
            );
            const commentsLoadingState = blogsFeedState.commentsLoadingState;

            const commentsCounts = getPostsCommentsCount(state, posts);

            const complaintsCounts = isAdmin
                ? getCommentComplaintsCounts(state, postIds, commentsLoadingState)
                : emptyComplaintsCounts;

            const postsSubtreeComplaintList = isNumber(pageRef)
                ? (blogsFeedPageState as BlogsFeedPageState).postsSubtreeComplaintList
                : undefined;

            if (
                category === BlogsFeedCategory.FLAGGED ||
                category === BlogsFeedCategory.FLAGGED_GROUPS
            ) {
                // ignore posts with no complaint
                posts = this.getPostsWithComplaintsCache(
                    () =>
                        posts.filter((p) => {
                            const complaints = findComplaintsNonDeleted(
                                state,
                                ComplaintTargetType.PhotoblogPost,
                                p.id,
                            );
                            const postSubtreeComplaints = postsSubtreeComplaintList
                                ? getComplaintsNonDeleted(
                                      getDenormalizedEntities<ComplaintEntity>(
                                          state,
                                          ComplaintWithoutTargetSchema,
                                          postsSubtreeComplaintList[p.id],
                                      ),
                                  )
                                : [];
                            return complaints.length > 0 || postSubtreeComplaints.length;
                        }),
                    posts,
                );
            }

            return {
                brandDaysPostId,
                category,
                commentsCounts,
                complaintsCounts,
                complaintsOpened: getComplaintsShownForBlogPosts(state, posts),
                postsSubtreeComplaintList,
                errorMessage: blogsFeedPageState.errorMessage,
                feedsTabsLastVisitCounts,
                hasComplaints: blogsFeedState.hasComplaints,
                hashtags: blogsFeedState.hashtags,
                isIOS: getRequestDeviceIsIOS(state),
                loadedAt,
                loadingState: hpMatchesCachedData
                    ? blogsFeedPageState.loadingState
                    : LoadingState.INIT,
                isMobile: getRequestDeviceMobile(state),
                pageRef: parsePageNumber(ownProps.location),
                requestUserPermissions,
                anyPageLoaded,
                posts,
                postType,
                query,
                requestUser,
                userAgent,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onDelete: blogsDeletePostTrigger,
            onIgnoreUser: blogsIgnoreUserTrigger,
            onLoad: blogsFeedFetchTrigger,
            onPostToggleMenuMore: (postId: number) => menuToggle(BLOG_POST_MENU_MORE(postId)),
            onPostToggleVisibilityMenuMore: (postId: number) =>
                menuToggle(BLOG_POST_VISIBILITY_MENU_MORE(postId)),
            onReport: complaintOpenTrigger,
            onScrollTo: blogsFeedScrollToTrigger,
            onSetDisplayingAtFeed: blogsSetDisplayingAtFeedTrigger,
            onSetVisibility: blogsSetPostVisibilityTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const categoryTitle = getCategoryTitle(props.category);
        return categoryTitle ? `${categoryTitle} - ${BLOGS_WALL}` : BLOGS_WALL;
    }

    public render(props: Props): false | JSX.Element {
        return <BlogsFeed {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        const { category, feedsTabsLastVisitCounts, requestUser } = props;
        return category === BlogsFeedCategory.ALL
            ? blogFeedHeaderTabs("all", requestUser, feedsTabsLastVisitCounts)
            : null;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        const { category } = props;
        return {
            activeSection: "photoblog",
            activeItem:
                category === BlogsFeedCategory.FLAGGED
                    ? HeaderMenuItem.BlogsFlagged
                    : category === BlogsFeedCategory.MY_ACTIVITY
                      ? HeaderMenuItem.BlogsMyActivity
                      : HeaderMenuItem.BlogFeeds,
        };
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { isMobile } = props;
        if (!isMobile) {
            return {
                zone: "Blogs Feed - Halfpage Sticky (Desktop)",
            };
        }
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: props.isMobile ? "Blogs Feed - Top 01 - Mobile" : "Blogs - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: props.isMobile ? "Blogs Feed - Interstitial" : "Blogs - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { posts, requestUser, isMobile } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if (!posts || posts.length < 10) {
            return null;
        }

        return {
            zone: "Blogs Feed - Anchor - Mobile",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.blogs.blogsFeed", reducer);
        store.injectSaga("containers.blogs.blogsFeed", saga);

        store.injectReducer("containers.blogs.blogsShared", blogsSharedReducer);
        store.injectSaga("containers.blogs.blogsShared", blogsSharedSaga);

        store.injectSaga("containers.blogs.shared.comments", commentsSaga);
    }
}
